import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ShipDialogService } from '@ship-ui/core/ship-dialog';
import { defer, finalize, map, of, switchMap } from 'rxjs';
import { resolveStatusText, STATUS_STATES } from '../../constants/status-states.constant';
import {
  DuplicatiServer,
  GetApiV1ProgressstateResponse,
  GetApiV1TaskByTaskidResponse,
  ServerStatusDto,
} from '../../openapi';
import { BytesPipe } from '../../pipes/byte.pipe';
import { ServerStateService } from '../../services/server-state.service';
import { Backup, BackupsState } from '../../states/backups.state';
import { SysinfoState } from '../../states/sysinfo.state';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
type Task = GetApiV1TaskByTaskidResponse;

export type Status = GetApiV1ProgressstateResponse & {
  task?: Task | null;
  backup?: Backup | null;
};

export type StatusWithContent = Status & {
  progress: number;
  statusText: string;
  fileStatusText: string;
  fileCountProgressText: string;
  actionText: string;
};

@Injectable({
  providedIn: 'root',
})
export class StatusBarState {
  #bytesPipe = inject(BytesPipe);
  #dupServer = inject(DuplicatiServer);
  #backupState = inject(BackupsState);
  #serverState = inject(ServerStateService);
  #dialog = inject(ShipDialogService);
  #sysinfo = inject(SysinfoState);
  #isFetching = signal(false);
  #statusData = signal<StatusWithContent | null>(null);

  statusData = this.#statusData.asReadonly();

  lastState: ServerStatusDto | null = null;
  serverState = computed(() => {
    const serverState = this.#serverState.serverState();

    if (!serverState) return this.lastState;

    serverState.ProposedSchedule =
      serverState.ProposedSchedule?.map((x) => ({
        ...x,
        backup: x.Item1 ? this.#backupState.getBackupById(x.Item1) : null,
      })) ?? null;

    this.lastState = serverState;

    return serverState;
  });

  hasProgressOnWebsocket = computed(
    () => this.#sysinfo.hasProgressSubscribeOption() && this.#serverState.getConnectionMethod() === 'websocket'
  );

  clientIsRunning = computed(() => this.serverState()?.ProgramState === 'Running');

  runningBackupId = computed(() => {
    const data = this.statusData();
    const isActive = !!(this.serverState()?.ActiveTask && data?.task?.Status && data?.task?.Status !== 'Completed');
    return isActive ? (data?.BackupID ?? null) : null;
  });

  isResuming = signal<boolean>(false);
  isFetching = this.#isFetching.asReadonly();
  connectionStatus = this.#serverState.connectionStatus;
  pollingInterval: number | undefined;
  #activeTaskId: number | null = null;

  #serverStateEffect = effect(() => {
    const serverState = this.#serverState.serverState();
    if (!serverState?.ActiveTask) this.#backupState.getBackups(true);

    if (this.hasProgressOnWebsocket()) {
      this.#serverState.subscribe('progress');
      this.#serverState.subscribe('taskqueue');
    }

    // A new task must not show the previous task's final state while its first progress fetch is pending
    const activeTaskId = serverState?.ActiveTask?.Item1 ?? null;
    if (activeTaskId !== this.#activeTaskId) {
      this.#activeTaskId = activeTaskId;
      if (activeTaskId !== null) this.#statusData.set(null);
    }

    if (serverState?.ActiveTask && this.pollingInterval === undefined) {
      this.startPollingProgress();
    } else if (!serverState?.ActiveTask) {
      this.stopPollingProgress();
    }
  });

  #progressStateEffect = effect(() => {
    const progressState = this.#serverState.progressState();
    const taskqueue = this.#serverState.taskQueueState();
    if (progressState) {
      const task = taskqueue?.find((x) => x.ID === progressState.TaskID) ?? null;
      this.#onProgressStateFetched({ ...progressState, task: task });
    }
  });

  setConnectionMethod(method: 'websocket' | 'longpoll') {
    this.#serverState.setConnectionMethod(method);
  }

  startPollingProgress() {
    // If the server pushes progress updates via WebSocket, we don't need to poll
    if (this.hasProgressOnWebsocket()) {
      this.#serverState.subscribe('progress');
    } else {
      this.pollingInterval = setInterval(() => {
        this.#getProgressState();
      }, 1000);
    }
  }

  stopPollingProgress() {
    if (this.pollingInterval === undefined) return;

    // The handle is reset so the next active task starts polling again
    clearInterval(this.pollingInterval);
    this.pollingInterval = undefined;
  }

  fetchProgressState() {
    this.#getProgressState();
  }

  pauseResume() {
    this.isResuming.set(true);

    return defer(() => this.#dupServer.postApiV1ServerstateResume()).pipe(finalize(() => this.isResuming.set(false)));
  }

  resumeDialogCheck(cb: Function) {
    if (!this.clientIsRunning()) {
      this.#dialog.open(ConfirmDialogComponent, {
        data: {
          title: $localize`Server paused`,
          message: $localize`Server is currently paused. Do you want to resume now?`,
          confirmText: $localize`Resume`,
          cancelText: $localize`No, I'll resume later`,
        },
        closed: (res) => {
          if (!res) return;
          this.pauseResume().subscribe({
            next: () => {
              cb();
            },
          });
        },
      });

      return;
    }

    cb();
  }

  #getProgressState() {
    this.#isFetching.set(true);
    defer(() => this.#dupServer.getApiV1Progressstate())
      .pipe(
        switchMap((x) => {
          const taskId = x.TaskID ?? null;
          if (taskId === null) return of(x);

          return defer(() => this.#dupServer.getApiV1TaskByTaskid({ path: { taskid: taskId } })).pipe(
            map((res) => ({ ...x, task: res ?? null }))
          );
        }),
        finalize(() => this.#isFetching.set(false))
      )
      .subscribe({
        next: (res) => {
          this.#onProgressStateFetched(res);
          if (this.serverState()?.ActiveTask == null) {
            this.stopPollingProgress();
          }
        },
        error: (err) => {
          if (err.status === 404) {
            this.stopPollingProgress();
          }
        },
      });
  }

  #onProgressStateFetched(status: Status) {
    const taskId = status.TaskID ?? null;
    const backupId = status.BackupID ?? null;

    if (taskId !== null && backupId !== null) {
      status.backup = this.#backupState.getBackupById(backupId);
    }

    this.#statusData.set({
      ...status,
      progress: this.#calculateProgress(status),
      statusText: this.#constructStatusText(status),
      fileStatusText: this.#constructFileStatus(status),
      fileCountProgressText: this.#constructFileCountProgress(status),
      actionText: status.backup?.Backup?.Name ?? $localize`Running task`,
    });
  }

  #calculateProgress(status: Status): number {
    let pg = -1;

    if (status.task && status) {
      if (
        status.Phase === 'Backup_ProcessingFiles' ||
        status.Phase === 'Restore_DownloadingRemoteFiles' ||
        status.Phase === 'Sync_ProcessingFiles'
      ) {
        if (status.StillCounting) {
          pg = 0;
        } else {
          const unaccountedbytes = status.CurrentFilecomplete ? 0 : status.CurrentFileoffset;
          const filesleft = status.TotalFileCount! - status.ProcessedFileCount!;
          const sizeleft = status.TotalFileSize! - status.ProcessedFileSize! - unaccountedbytes!;
          pg = status.TotalFileSize! > 0 ? (status.ProcessedFileSize! + unaccountedbytes!) / status.TotalFileSize! : 0;

          if (status.ProcessedFileCount === 0) {
            pg = 0;
          } else if (pg >= 0.9) {
            pg = 0.9;
          }
        }
      } else if (status.Phase === 'Sync_CountingFiles') {
        pg = 0;
      } else if (
        status.Phase === 'Backup_Finalize' ||
        status.Phase === 'Backup_WaitForUpload' ||
        status.Phase === 'Sync_WaitForUpload'
      ) {
        pg = 0.9;
      } else if (status.Phase === 'Backup_Delete' || status.Phase === 'Backup_Compact') {
        pg = 0.95;
      } else if (status.Phase === 'Backup_VerificationUpload' || status.Phase === 'Backup_PostBackupVerify') {
        pg = 0.98;
      } else if (status.Phase === 'Backup_Complete' || status.Phase === 'Sync_Complete') {
        pg = 1;
      } else if (status.Phase === 'Backup_RemoteSynchronization') {
        pg = status.OverallProgress! > 0 ? status.OverallProgress! : 0;
      } else if (status.OverallProgress! > 0) {
        pg = status.OverallProgress!;
      }
    }

    return pg;
  }

  #constructSpeedText(status: Status): string {
    const aggregateSpeed =
      (status.ActiveTransfers ?? []).map((x) => x.BackendSpeed).reduce((acc, curr) => (acc ?? 0) + (curr ?? 0), 0) ??
      -1;

    const speed = aggregateSpeed <= 0 ? status.BackendSpeed : aggregateSpeed;
    return (speed ?? -1) < 0 ? '' : ` at ${this.#bytesPipe.transform(speed)}/s`;
  }

  #constructFileStatus(status: Status): string {
    const fileOffset =
      (['Backup_WaitForUpload', 'Backup_ProcessingFiles', 'Sync_WaitForUpload', 'Sync_ProcessingFiles'].includes(
        status.Phase!
      )
        ? status?.BackendFileProgress
        : status.CurrentFileoffset) ?? 0;

    const fileSize =
      (['Backup_WaitForUpload', 'Backup_ProcessingFiles', 'Sync_WaitForUpload', 'Sync_ProcessingFiles'].includes(
        status.Phase!
      )
        ? status?.BackendFileSize
        : status.CurrentFilesize) ?? 0;

    if (!status.CurrentFilename) return '';
    const fileName =
      status.CurrentFilename.length > 42 ? '...' + status.CurrentFilename.slice(-42) : status.CurrentFilename;

    if (fileSize <= 0)
      return $localize`${fileName} | File processed: ${this.#bytesPipe.transform(fileOffset)}/${this.#bytesPipe.transform(fileSize)}`;

    const percentage = ((fileOffset / fileSize) * 100).toFixed(1);

    return $localize`${fileName} | File processed: ${this.#bytesPipe.transform(fileOffset)}/${this.#bytesPipe.transform(fileSize)} - ${percentage}%`;
  }

  #constructFileCountProgress(status: Status): string {
    return $localize`Files processed ${status.ProcessedFileCount}/${status.TotalFileCount}`;
  }

  #constructStatusText(status: Status): string {
    let text = 'Running …';

    if (status.task && status) {
      text = resolveStatusText(status.Phase, 'Running …');

      // If there is nothing to process, just return the base text
      // This happens during the initial phases of a backup/restore
      if (status.TotalFileCount === 0) return text;

      if (
        status.Phase === 'Backup_ProcessingFiles' ||
        status.Phase === 'Restore_DownloadingRemoteFiles' ||
        status.Phase === 'Sync_ProcessingFiles' ||
        status.Phase === 'Sync_CountingFiles'
      ) {
        if (status.StillCounting || status.Phase === 'Sync_CountingFiles') {
          text = `Counting (${status.TotalFileCount} files found, ${this.#bytesPipe.transform(status.TotalFileSize)})`;
        } else {
          const unaccountedbytes = status.CurrentFilecomplete ? 0 : status.CurrentFileoffset;
          const filesleft = Math.max(0, status.TotalFileCount! - status.ProcessedFileCount!);
          const sizeleft = Math.max(0, status.TotalFileSize! - status.ProcessedFileSize! - unaccountedbytes!);
          const restoringText = status.Phase === 'Restore_DownloadingRemoteFiles' ? 'Restoring: ' : '';
          const speedTxt = this.#constructSpeedText(status);

          const isProcessing = status.Phase === 'Backup_ProcessingFiles' || status.Phase === 'Sync_ProcessingFiles';
          const hasTransfers = (status.ActiveTransfers?.length ?? 0) > 0 || (status.BackendSpeed ?? -1) > 0;

          if (isProcessing && filesleft === 0) {
            // All files are handed off, but processing can continue without any upload in flight.
            // Only call it an upload when a transfer is active; otherwise keep the phase text,
            // which is what the status page shows.
            if (hasTransfers || (status.BackendIsBlocking ?? false)) text = `Completing upload ${speedTxt}`;
          } else if (status.BackendIsBlocking ?? false) text = `Waiting for transfers ${speedTxt}`;
          else text = `${restoringText}${filesleft} files (${this.#bytesPipe.transform(sizeleft)}) to go ${speedTxt}`;
        }
      } else if (status.Phase === 'Backup_WaitForUpload' || status.Phase === 'Sync_WaitForUpload') {
        const speedTxt = this.#constructSpeedText(status);
        if (speedTxt !== '') text = `Waiting for upload to finish ${speedTxt}`;
      } else if (status.Phase === 'Backup_RemoteSynchronization') {
        const destCount = status.RemoteSyncDestinationCount ?? 0;
        const destIndex = status.RemoteSyncDestinationIndex ?? 0;
        const destLabel = destCount > 1 ? ` (${destIndex}/${destCount})` : '';
        if (status.TotalFileCount! > 0) {
          const filesleft = Math.max(0, status.TotalFileCount! - status.ProcessedFileCount!);
          const sizeleft = Math.max(0, status.TotalFileSize! - status.ProcessedFileSize!);
          const speedTxt = this.#constructSpeedText(status);
          text = `Synchronizing secondary destination${destLabel}: ${filesleft} files (${this.#bytesPipe.transform(sizeleft)}) to go ${speedTxt}`;
        } else if (destLabel) {
          text = `${STATUS_STATES[status.Phase]}`.replace('…', `${destLabel} …`);
        }
      }
    }
    return text;
  }
}
